import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

// Un pulsante che apre un menu sotto di sé (lo schema «menu button» di WAI-ARIA), con le regole di tastiera e di fuoco in un posto
// solo: «Altro» della Smart List (`SmartListDialogs.tsx`) e «Altro» della testata della scheda (`LeadHeaderMore.tsx`). Aperto, il
// fuoco va sulla prima voce; frecce, Inizio e Fine si muovono fra le voci (in giro); Esc chiude e torna al pulsante; Tab chiude e
// lascia andare il fuoco; un clic fuori dal pulsante e dal menu chiude. Le voci sono i `button` con `role="menuitem"` non spenti.
// «Quale numero chiamare» di «Chiama» (`CallButton.tsx`) ha ancora le sue regole, senza Tab, Inizio e Fine: passarlo qui le
// cambierebbe.
// Chi lo usa mette `wrapperRef` sull'elemento che contiene pulsante e menu, `buttonRef` e `buttonProps` sul pulsante, `menuRef` e
// `menuProps` sul menu.

const ITEMS = "button[role='menuitem']:not(:disabled)";

export function useMenuButton() {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>(ITEMS) ?? []);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>(ITEMS)?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapperRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  /** Chiude il menu; con `focusButton` il fuoco torna al pulsante (Esc, una voce scelta che apre una finestra). */
  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === "Tab") {
      close(false);
      return;
    }
    const buttons = items();
    const index = buttons.findIndex((button) => button === document.activeElement);
    let next: HTMLButtonElement | undefined;
    if (event.key === "ArrowDown") next = buttons[(index + 1) % buttons.length];
    else if (event.key === "ArrowUp") next = buttons[(index - 1 + buttons.length) % buttons.length];
    else if (event.key === "Home") next = buttons[0];
    else if (event.key === "End") next = buttons.at(-1);
    if (next) {
      event.preventDefault();
      next.focus();
    }
  }

  return {
    open,
    close,
    wrapperRef,
    buttonRef,
    menuRef,
    buttonProps: {
      "aria-haspopup": "menu" as const,
      "aria-expanded": open,
      "aria-controls": open ? menuId : undefined,
      onClick: () => setOpen((current) => !current),
    },
    menuProps: { id: menuId, role: "menu", tabIndex: -1, onKeyDown },
  };
}
